import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { useQuery, invalidate } from "@/lib/useQuery";
import type { Inbox } from "@/lib/types";
import { Modal, Field, toast } from "@/components/ui";
import { RichEditor, type RichEditorHandle } from "@/components/RichEditor";
import { SendControls, type SendOptions } from "./SendControls";
import { TemplatePicker } from "./TemplatePicker";
import { useMe } from "@/app/MeContext";
import { AttachPicker, AttachmentPills, toSendPayload, type OutAtt } from "./AttachPicker";
import { startWithSignature, withSignature } from "@/lib/signature";

export function ComposeModal({ accountId, partnerId, to = "", subject = "", onClose, onSent }: { accountId?: string; partnerId?: string; to?: string; subject?: string; onClose: () => void; onSent?: () => void }) {
  const me = useMe();
  const { data: inboxes } = useQuery<Inbox[]>("inboxes", () => api.get<Inbox[]>("/inbox/inboxes"));
  const [f, setF] = useState({ to, cc: "", subject, inboxId: "" });
  const [atts, setAtts] = useState<OutAtt[]>([]);
  const [busy, setBusy] = useState(false);
  const [showCc, setShowCc] = useState(false);
  const editor = useRef<RichEditorHandle>(null);
  const splitAddrs = (s: string) => s.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
  const send = async (opts: SendOptions) => {
    const html = editor.current?.getHtml() ?? "";
    if (!f.to.trim() || !f.subject.trim()) { toast("To and subject are required", true); return; }
    if (!html.replace(/<[^>]+>/g, "").trim()) { toast("Write something first", true); return; }
    setBusy(true);
    try {
      const r = await api.post<{ scheduled: boolean }>("/inbox/send", { inboxId: f.inboxId || undefined, accountId: accountId ?? null, partnerId: partnerId ?? null, to: splitAddrs(f.to), cc: splitAddrs(f.cc), subject: f.subject, html, ...toSendPayload(atts), scheduledAt: opts.scheduledAt ?? null, remindDays: opts.remindDays ?? null });
      toast(r.scheduled ? "Email scheduled" : "Email sent");
      invalidate("threads"); invalidate("badges"); if (accountId) { invalidate(`account:${accountId}`); invalidate(`activity:${accountId}`); invalidate(`emails:${accountId}`); } if (partnerId) { invalidate(`partner:${partnerId}`); invalidate(`emails:${partnerId}`); }
      onSent?.(); onClose();
    } catch (e) { toast((e as Error).message, true); } finally { setBusy(false); }
  };
  return (
    <Modal title="New email" onClose={onClose} wide>
      <div className="form-grid">
        {inboxes && inboxes.length > 1 && (
          <Field label="From"><select className="select" value={f.inboxId} onChange={(e) => setF((x) => ({ ...x, inboxId: e.target.value }))}><option value="">Default ({me.name})</option>{inboxes.map((ib) => <option key={ib.id} value={ib.id}>{ib.address}</option>)}</select></Field>
        )}
        <Field label="To"><input className="input" autoFocus={!to} placeholder="name@company.com" value={f.to} onChange={(e) => setF((x) => ({ ...x, to: e.target.value }))} /></Field>
        {showCc ? <Field label="Cc"><input className="input" value={f.cc} onChange={(e) => setF((x) => ({ ...x, cc: e.target.value }))} /></Field> : <div className="field"><label>&nbsp;</label><button className="btn-link small" style={{ textAlign: "left", height: 32 }} onClick={() => setShowCc(true)}>Add Cc</button></div>}
      </div>
      <Field label="Subject"><input className="input" value={f.subject} onChange={(e) => setF((x) => ({ ...x, subject: e.target.value }))} /></Field>
      <div className="reply-box" style={{ margin: 0 }}>
        <RichEditor ref={editor} minHeight={180} autoFocus={!!to} initialHtml={startWithSignature(me.signature)} />
        <AttachmentPills value={atts} onChange={setAtts} />
        <div className="tools">
          <TemplatePicker accountId={accountId} onPick={(t) => { if (t.subject && !f.subject) setF((x) => ({ ...x, subject: t.subject })); editor.current?.setHtml(withSignature(t.body, me.signature)); }} />
          <AttachPicker value={atts} onChange={setAtts} accountId={accountId} partnerId={partnerId} />
          <SendControls busy={busy} onSend={send} />
        </div>
      </div>
    </Modal>
  );
}
